import { useCallback, useEffect, useRef, useState } from 'react'

import { uploadsApi, type JobType, type UploadCompletePart } from '../services/api/domains/uploads.api'
import { uploadPartWithRetry, type UploadPartHandle } from './use-upload-file.helpers'

export type UploadStatus = 'idle' | 'initializing' | 'uploading' | 'completing' | 'done' | 'aborted' | 'error'

export interface UseUploadFileStartArgs {
  file: File
  type: JobType
  input: Record<string, unknown>
}

export interface UseUploadFileStartResult {
  jobId: string
  s3Bucket: string
  s3Key: string
}

export interface UseUploadFileResult {
  status: UploadStatus
  jobId: string | null
  s3Bucket: string | null
  s3Key: string | null
  bytesUploaded: number
  bytesTotal: number
  percent: number
  error: Error | null
  start: (args: UseUploadFileStartArgs) => Promise<UseUploadFileStartResult>
  abort: () => Promise<void>
  reset: () => void
}

const PROGRESS_THROTTLE_MS = 100 // ~10 updates/sec

export function useUploadFile(): UseUploadFileResult {
  const [status, setStatus] = useState<UploadStatus>('idle')
  const [jobId, setJobId] = useState<string | null>(null)
  const [s3Bucket, setS3Bucket] = useState<string | null>(null)
  const [s3Key, setS3Key] = useState<string | null>(null)
  const [bytesUploaded, setBytesUploaded] = useState(0)
  const [bytesTotal, setBytesTotal] = useState(0)
  const [error, setError] = useState<Error | null>(null)

  const abortRef = useRef(false)
  const currentHandleRef = useRef<UploadPartHandle>({ xhr: null })
  const partProgressRef = useRef<Record<number, number>>({})
  const completedBytesRef = useRef(0)
  const lastProgressTsRef = useRef(0)
  const activeJobIdRef = useRef<string | null>(null)
  const inFlightRef = useRef(false)

  const commitProgress = useCallback((force: boolean) => {
    const now = Date.now()
    if (!force && now - lastProgressTsRef.current < PROGRESS_THROTTLE_MS) return
    lastProgressTsRef.current = now
    let inflight = 0
    for (const v of Object.values(partProgressRef.current)) inflight += v
    setBytesUploaded(completedBytesRef.current + inflight)
  }, [])

  const reset = useCallback(() => {
    abortRef.current = false
    currentHandleRef.current = { xhr: null }
    partProgressRef.current = {}
    completedBytesRef.current = 0
    lastProgressTsRef.current = 0
    activeJobIdRef.current = null
    inFlightRef.current = false
    setStatus('idle')
    setJobId(null)
    setS3Bucket(null)
    setS3Key(null)
    setBytesUploaded(0)
    setBytesTotal(0)
    setError(null)
  }, [])

  const abort = useCallback(async () => {
    if (!inFlightRef.current && status !== 'uploading' && status !== 'initializing' && status !== 'completing') {
      return
    }

    abortRef.current = true
    const xhr = currentHandleRef.current.xhr

    if (xhr) {
      try {
        xhr.abort()
      } catch {
        // ignore
      }
    }

    const id = activeJobIdRef.current

    if (id) {
      try {
        await uploadsApi.abort(id)
      } catch {
        // best-effort
      }
    }

    setStatus('aborted')
    inFlightRef.current = false
  }, [status])

  const start = useCallback(
    async ({ file, type, input }: UseUploadFileStartArgs) => {
      // Prevent concurrent uploads (e.g. double-click on Import button)
      if (inFlightRef.current) throw new Error('Upload already in progress')

      // Reset transient state but keep component mounted.
      abortRef.current = false
      currentHandleRef.current = { xhr: null }
      partProgressRef.current = {}
      completedBytesRef.current = 0
      lastProgressTsRef.current = 0
      inFlightRef.current = true
      setError(null)
      setBytesUploaded(0)
      setBytesTotal(file.size)
      setJobId(null)
      setStatus('initializing')

      let initializedJobId: string | null = null

      try {
        const initRes = await uploadsApi.init({
          filename: file.name,
          size: file.size,
          contentType: file.type || 'application/octet-stream',
          type,
          input,
        })
        const init = initRes.data
        initializedJobId = init.jobId
        activeJobIdRef.current = init.jobId
        setJobId(init.jobId)
        setS3Bucket(init.bucket)
        setS3Key(init.key)
        setStatus('uploading')

        const completedParts: UploadCompletePart[] = []

        for (const { partNumber, url } of init.urls) {
          if (abortRef.current) throw new Error('Aborted by user')
          const start = (partNumber - 1) * init.partSize
          const end = Math.min(start + init.partSize, file.size)
          const blob = file.slice(start, end)
          const partSize = end - start
          partProgressRef.current[partNumber] = 0

          const etag = await uploadPartWithRetry(
            {
              url,
              blob,
              handle: currentHandleRef.current,
              onProgress: loaded => {
                partProgressRef.current[partNumber] = Math.min(loaded, partSize)
                commitProgress(false)
              },
            },
            3,
            () => abortRef.current,
          )

          // Mark this part fully done; fold into completedBytes baseline.
          delete partProgressRef.current[partNumber]
          completedBytesRef.current += partSize
          commitProgress(true)
          completedParts.push({ partNumber, etag })
          currentHandleRef.current.xhr = null
        }

        if (abortRef.current) throw new Error('Aborted by user')

        setStatus('completing')
        await uploadsApi.complete(init.jobId, { parts: completedParts })
        setStatus('done')
        inFlightRef.current = false

        return { jobId: init.jobId, s3Bucket: init.bucket, s3Key: init.key }
      } catch (err) {
        inFlightRef.current = false
        const e = err instanceof Error ? err : new Error(String(err))
        const isAbort = abortRef.current || /aborted/i.test(e.message)

        if (initializedJobId && !isAbort) {
          // Best-effort backend cleanup on error
          uploadsApi.abort(initializedJobId).catch(() => undefined)
        }

        if (isAbort) {
          setStatus('aborted')
        } else {
          setError(e)
          setStatus('error')
        }

        throw e
      }
    },
    [commitProgress],
  )

  // Cleanup on unmount: abort any in-flight upload.
  useEffect(() => {
    return () => {
      if (!inFlightRef.current) return
      abortRef.current = true
      const xhr = currentHandleRef.current.xhr

      if (xhr) {
        try {
          xhr.abort()
        } catch {
          // ignore
        }
      }

      const id = activeJobIdRef.current

      if (id) {
        uploadsApi.abort(id).catch(() => undefined)
      }
    }
  }, [])

  const percent = bytesTotal > 0 ? Math.min(100, Math.round((bytesUploaded / bytesTotal) * 100)) : 0

  return {
    status,
    jobId,
    s3Bucket,
    s3Key,
    bytesUploaded,
    bytesTotal,
    percent,
    error,
    start,
    abort,
    reset,
  }
}
